import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
    Card,
    CardContent,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Box,
    Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const features = [
    "Experienced and Certified Painters",
    "Top-Quality Paints and Materials",
    "Affordable and Transparent Pricing",
    "Timely Project Completion",
    "Customer Satisfaction Guarantee",
];

const WhyChoose = () => {
    const navigate = useNavigate();

    // ✅ Mount delay fix to prevent empty render
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <>
            <Helmet>
                <title>Why Choose PM Painters? | Professional Painting Experts</title>
                <meta
                    name="description"
                    content="Discover what sets PM Painters apart — skilled professionals, premium materials, on-time delivery, and affordable rates."
                />
                <meta
                    name="keywords"
                    content="best painters India, why choose PM Painters, professional painters Ahmedabad, trusted painting company"
                />
            </Helmet>

            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: (theme) => theme.spacing(5),
                    overflow: "hidden",
                }}
            >
                {/* ✅ Render after mount to avoid blank screen */}
                {mounted && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        style={{ width: "100%", maxWidth: 600 }}
                    >
                        <Card
                            sx={{
                                p: { xs: 2, sm: 4 },
                                borderRadius: 4,
                                boxShadow: 4,
                                textAlign: "center",
                                backgroundColor: (theme) => theme.palette.background.paper,
                            }}
                        >
                            <CardContent>
                                {/* ✏️ SVG Brush Stroke Animation */}
                                <motion.svg
                                    viewBox="0 0 400 100"
                                    xmlns="http://www.w3.org/2000/svg"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 2 }}
                                    style={{
                                        stroke: "#1A237E",
                                        strokeWidth: 4,
                                        fill: "none",
                                        width: "100%",
                                        maxWidth: "300px",
                                        marginBottom: 24,
                                        display: "block",
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                    }}
                                >
                                    <path d="M10 80 C 40 10, 65 10, 95 80 S 150 150, 180 80 S 240 10, 270 80 S 320 150, 350 80" />
                                </motion.svg>

                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <Typography
                                        variant="h4"
                                        gutterBottom
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: { xs: "1.8rem", sm: "2.2rem" }, // already great
                                            color: "text.primary",
                                        }}
                                    >
                                        Why Choose PM Enterprise?
                                    </Typography>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2, duration: 0.5 }}
                                >
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        mb={3}
                                        sx={{ fontSize: { xs: "0.95rem", sm: "1.05rem" } }}
                                    >
                                        We provide high-quality painting services designed to exceed your expectations.
                                    </Typography>
                                </motion.div>

                                <List>
                                    {features.map((feature, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                                        >
                                            <ListItem disableGutters>
                                                <ListItemIcon>
                                                    <CheckCircleIcon color="primary" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={feature}
                                                    primaryTypographyProps={{
                                                        fontSize: { xs: "0.95rem", sm: "1.05rem" },
                                                    }}
                                                />
                                            </ListItem>
                                        </motion.div>
                                    ))}
                                </List>

                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.4, duration: 0.4 }}
                                >
                                    <Box mt={4}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            sx={{
                                                fontSize: { xs: "0.85rem", sm: "1rem" },
                                                px: { xs: 2, sm: 4 },
                                                py: { xs: 1, sm: 1.5 },
                                                transition: "background-color 0.3s",
                                                "&:hover": {
                                                    backgroundColor: (theme) => theme.palette.secondary.main,
                                                },
                                            }}
                                            onClick={() => navigate("/paintcalculator")}
                                        >
                                            Get a Free Estimate
                                        </Button>
                                    </Box>
                                </motion.div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
            </Box>
        </>
    );
};

export default WhyChoose;
